import { observable, action, computed } from 'mobx';
import { deserialise } from 'kitsu-core';

import Sample from './Sample';
import Reprocess from './Reprocess';
import FractionType, { EvaluationResult, SplitMethod } from 'types/model/fraction';

import Routes from 'routes';
import { customFetch } from 'utils/fetch';
import FractionsStore from 'productionOrders/stores/FractionsStore';
import { createSampleFactory } from 'utils/factories/createSampleFactory';
import SampleBuilder from 'utils/builders/SampleBuilder';

export interface SplitParams {
  split_method: SplitMethod;
  bags_per_group?: number;
  parts?: number;
}

class Fraction {
  public milling_order_id: number;
  public id: number;
  public node_identifier: string;
  public depth: number;
  // PLA-860 v2: las subdivisiones nuevas no tienen muestra.
  public sample?: Sample;
  public parent?: Fraction;
  public root?: Fraction;
  public fractionsStore?: FractionsStore;
  @observable public subfractions: Fraction[];
  @observable public weight: number;
  @observable public rejected: number;
  @observable public remaining?: number;
  @observable public reprocesses: Reprocess[];
  // PLA-860 v2: division y resultado Aprobado/Rechazado de las subdivisiones.
  @observable public splitMethod?: SplitMethod | null;
  @observable public splittableWeight: number;
  @observable public evaluationResult?: EvaluationResult | null;
  @observable public evaluationImplicit: boolean;
  @observable public evaluatedAt?: string | null;
  @observable public evaluatedByName?: string | null;
  @observable public rejectedSubdivisionsWeight?: number | null;
  @observable public requestPending = false;

  constructor(
    fraction: FractionType,
    parent?: Fraction,
    root?: Fraction,
    fractionsStore?: FractionsStore
  ) {
    this.id = fraction.id;
    this.node_identifier = fraction.node_identifier;
    this.depth = fraction.depth;
    this.weight = parseFloat(fraction.weight);
    this.rejected = fraction.rejected;
    this.remaining = fraction.remaining;
    this.parent = parent;
    this.root = root;
    this.milling_order_id = fraction.milling_order_id;
    this.fractionsStore = fractionsStore;

    if (fraction.sample) {
      const sampleBuilder = new SampleBuilder(fraction.sample);

      this.sample = createSampleFactory(fraction.sample, sampleBuilder);
    }

    this.assignSubdivisionAttributes(fraction);

    this.subfractions = observable.array(this.buildSubfractions(fraction));

    const reprocesses = fraction.fraction_reprocesses?.map(
      (reprocess) => new Reprocess(reprocess, this)
    );
    this.reprocesses = observable.array(reprocesses);
  }

  private buildSubfractions(fraction: FractionType): Fraction[] {
    // If there is no Parent, it's a main fraction, thus the root is 'this'
    const rootFraction = this.parent ? this.root : this;

    return (fraction.subfractions || []).map(
      (subfraction) => new Fraction(subfraction, this, rootFraction)
    );
  }

  @action private assignSubdivisionAttributes(fraction: FractionType) {
    this.splitMethod = fraction.split_method;
    this.splittableWeight = fraction.splittable_weight ?? this.weight;
    this.evaluationResult = fraction.evaluation_result;
    this.evaluationImplicit = !!fraction.evaluation_implicit;
    this.evaluatedAt = fraction.evaluated_at;
    this.evaluatedByName = fraction.evaluated_by_name;
    this.rejectedSubdivisionsWeight = fraction.rejected_subdivisions_weight;
  }

  // PLA-860 v2: el servidor devuelve la fraccion principal con todo su arbol; se reemplaza para
  // refrescar las subdivisiones y el rechazo calculado de un solo golpe.
  @action private applyRootResponse(serialisedRoot) {
    const target = this.root || this;
    const fraction: FractionType = deserialise(serialisedRoot).data;

    target.weight = parseFloat(fraction.weight);
    target.rejected = fraction.rejected;
    target.remaining = fraction.remaining;
    target.assignSubdivisionAttributes(fraction);
    target.subfractions = observable.array(target.buildSubfractions(fraction));
  }

  @action private async requestRootChange(
    route: string,
    body: object,
    method: 'POST' | 'PATCH' | 'DELETE'
  ): Promise<string[]> {
    this.requestPending = true;

    try {
      const response = await customFetch(route, body, method);

      if (response.success) {
        this.applyRootResponse(response.fraction);
        return [];
      }

      return response.errors || [response.error];
    } finally {
      this.requestPending = false;
    }
  }

  // PLA-860 v2 (D1): divide la fraccion por sacos o por cantidad de fracciones.
  @action split = async (params: SplitParams): Promise<string[]> => {
    const errors = await this.requestRootChange(
      Routes.split_api_v1_milling_order_fraction_path(this.milling_order_id, this.id),
      params,
      'POST'
    );

    if (errors.length === 0) {
      this.finishLastReprocess();
    }

    return errors;
  };

  // PLA-860 v2 (D7): deshace una division sin resultados.
  @action undoSplit = async (): Promise<string[]> =>
    this.requestRootChange(
      Routes.split_api_v1_milling_order_fraction_path(this.milling_order_id, this.id),
      {},
      'DELETE'
    );

  // PLA-860 v2 (V2): Aprobado / Rechazado de una subdivision.
  @action evaluate = async (result: EvaluationResult): Promise<string[]> =>
    this.requestRootChange(
      Routes.evaluate_api_v1_milling_order_fraction_path(this.milling_order_id, this.id),
      { result },
      'PATCH'
    );

  @action finishLastReprocess = () => {
    if (this.lastReprocess && !this.lastReprocess.finished) {
      this.lastReprocess.setFinished();
      this.lastReprocess.synchronize();
    }
  };

  @action updateFraction = async () => {
    if (!this.canSync) {
      return;
    }

    const route = Routes.api_v1_milling_order_fraction_path(
      this.milling_order_id,
      this.id
    );

    const response = await customFetch(
      route,
      {
        weight: this.weight,
        rejected: this.rejected,
      },
      'PATCH'
    );

    if (!response.success) {
      window.location.reload();
      return;
    }

    // PLA-860 v2 (D3): el peso de una subdivision rechazada forma parte del rechazo de la raiz,
    // que el servidor ya recalculo.
    if (this.evaluationResult === 'rejected' && this.root) {
      this.root.syncRejectedFromLeaves();
    }
  };

  @action syncRejectedFromLeaves() {
    const rejected = this.leaves
      .filter((leaf) => leaf.evaluationResult === 'rejected')
      .reduce((total, leaf) => total + leaf.weight, 0);

    this.rejected = Math.min(parseFloat(rejected.toFixed(2)), this.weight);
    this.rejectedSubdivisionsWeight = this.rejected;
  }

  @action setWeight(weight: number) {
    this.weight = weight;
  }

  @action setRejectedWeight(rejectedWeight: number) {
    if (rejectedWeight <= this.weight) {
      this.rejected = rejectedWeight;
    }
  }

  @action createReprocess = async () => {
    const route = Routes.api_v1_fraction_fraction_reprocesses_path(this.id);

    const response = await customFetch(
      route,
      {
        fraction_id: this.id,
        initial: this.totalGreen,
      },
      'POST'
    );

    if (response.success) {
      const reprocess = new Reprocess(
        deserialise(response.fraction_reprocess).data,
        this
      );

      this.reprocesses.push(reprocess);
    }
  };

  @computed public get isMain(): boolean {
    return this.parent === undefined;
  }

  @computed public get canSync(): boolean {
    return (
      !this.exceedsParentMaxWeight &&
      !this.exceedsRejectedMaxWeight &&
      (this.weight !== 0 || this.rejected !== 0)
    );
  }

  @computed public get sensorialFailed(): boolean {
    return !!this.sample?.current_sensorial_analysis?.analysisFailed;
  }

  @computed public get showRejectedInput(): boolean {
    return this.sensorialFailed;
  }

  // PLA-860 v2 (D5, V4): se divide una fraccion principal con sensorial fallido o una
  // subdivision marcada Rechazado, mientras no este dividida.
  @computed public get showSplitButton(): boolean {
    if (this.subfractions.length > 0) {
      return false;
    }

    if (this.isMain || !this.evaluationResult) {
      return this.sensorialFailed;
    }

    return this.evaluationResult === 'rejected';
  }

  // PLA-860 v2 (V2): las subdivisiones sin dividir se marcan Aprobado/Rechazado.
  @computed public get isSubdivisionLeaf(): boolean {
    return !this.isMain && this.subfractions.length === 0;
  }

  @computed public get leaves(): Fraction[] {
    if (this.subfractions.length === 0) {
      return [this];
    }

    return this.subfractions.reduce(
      (leaves, subfraction) => leaves.concat(subfraction.leaves),
      [] as Fraction[]
    );
  }

  @computed public get descendants(): Fraction[] {
    return this.subfractions.reduce(
      (descendants, subfraction) =>
        descendants.concat(subfraction, subfraction.descendants),
      [] as Fraction[]
    );
  }

  // PLA-860 v2 (D7, V9): deshacer solo si ninguna subdivision tiene resultado ni analisis.
  @computed public get canUndoSplit(): boolean {
    return (
      this.subfractions.length > 0 &&
      this.descendants.every(
        (fraction) =>
          !fraction.evaluationResult &&
          !fraction.sample?.current_roasting?.id &&
          !fraction.sample?.current_sensorial_analysis?.id
      )
    );
  }

  // PLA-860 v3 (D1): las subdivisiones no reciben el store; lo toman de su fraccion principal.
  @computed private get store(): FractionsStore | undefined {
    return this.fractionsStore || this.root?.fractionsStore;
  }

  @computed public get hasChildren(): boolean {
    return this.subfractions.length > 0 || this.reprocesses.length > 0;
  }

  @computed public get expanded(): boolean {
    return !!this.store?.isExpanded(this);
  }

  @action public setExpanded(expanded: boolean) {
    this.store?.setExpanded(this, expanded);
  }

  @action public toggleExpanded = () => {
    this.setExpanded(!this.expanded);
  };

  // PLA-860 v3 (W5, D6): resumen de la fila contraida, sobre las hojas del grupo.
  @computed public get subdivisionSummary(): {
    count: number;
    rejectedCount: number;
    rejectedWeight: number;
  } | null {
    if (this.subfractions.length === 0) {
      return null;
    }

    const rejected = this.leaves.filter(
      (leaf) =>
        leaf.evaluationResult === 'rejected' ||
        (!leaf.evaluationResult && leaf.sensorialFailed)
    );

    return {
      count: this.leaves.length,
      rejectedCount: rejected.length,
      rejectedWeight: rejected.reduce(
        (total, leaf) =>
          total + (leaf.evaluationResult ? leaf.weight : leaf.rejected || 0),
        0
      ),
    };
  }

  @computed public get isEvaluated(): boolean {
    return (
      !!this.evaluationResult || !!this.sample?.current_sensorial_analysis?.finalized
    );
  }

  // PLA-860 v2 (D6): subdivisiones que se aprobaran al confirmar las fracciones.
  @computed public get pendingSubdivisions(): Fraction[] {
    return this.leaves.filter((leaf) => !leaf.isMain && !leaf.isEvaluated);
  }

  // PLA-860 v2 (V13): kg de las divisiones que no quedaron en ninguna subdivision.
  @computed public get unassignedWeight(): number {
    const own = this.splitMethod
      ? Math.max(
          this.splittableWeight -
            this.subfractions.reduce(
              (total, subfraction) => total + subfraction.weight,
              0
            ),
          0
        )
      : 0;

    return this.subfractions.reduce(
      (total, subfraction) => total + subfraction.unassignedWeight,
      own
    );
  }

  // PLA-860 v2 (D3): aviso cuando el rechazo ajustado a mano difiere de la suma calculada.
  @computed public get showRejectedSumHint(): boolean {
    return (
      this.isMain &&
      this.subfractions.length > 0 &&
      this.rejectedSubdivisionsWeight !== null &&
      this.rejectedSubdivisionsWeight !== undefined &&
      Math.abs(this.rejectedSubdivisionsWeight - (this.rejected || 0)) > 0.001
    );
  }

  @computed public get displayIdentifier(): string {
    const sample = this.sample || this.root?.sample;

    return sample
      ? `${this.node_identifier}-${sample.local_identifier}`
      : `${this.node_identifier}`;
  }

  @computed public get excelsoInputDisabled() {
    if (this.isMain) {
      return (
        !this.isLast ||
        (this.isLast && (!!this.subfractions.length || !!this.reprocesses.length))
      );
    } else {
      return !!this.subfractions.length;
    }
  }

  // PLA-860 v2 (D5): en la fraccion principal el rechazo sigue editable despues de dividir.
  @computed public get rejectedInputDisabled() {
    return this.isMain ? false : !!this.subfractions.length;
  }

  @computed get isLast() {
    if (this.fractionsStore?.fractions) {
      return (
        this.fractionsStore.fractions[this.fractionsStore.fractions.length - 1] === this
      );
    } else {
      return false;
    }
  }

  @computed public get remainingWeight(): number {
    let deductionWeight = this.totalRejected;

    if (this.lastReprocess && this.lastReprocess.analysisStarted) {
      deductionWeight += this.lastReprocess.weight;
    }

    return Math.max(this.weight - deductionWeight, 0);
  }

  @computed public get hasUnfinishedReprocesses() {
    return (
      this.reprocesses && this.reprocesses.some((reprocess) => !reprocess.inputValuesSet)
    );
  }
  // PLA-860 v2: el rechazo de una fraccion dividida sale de sus subdivisiones, asi que ya no
  // condiciona la division.
  @computed public get isSplittable() {
    if (!this.isMain) {
      return this.weight > 0;
    }

    return (
      this.weight > 0 &&
      ((this.lastReprocess && this.lastReprocess.hasWeight) || !this.lastReprocess)
    );
  }

  @computed private get adjacentFractions() {
    let adjacentFractions: Fraction[] = [];

    if (this.parent && this.root) {
      adjacentFractions = this.parent.subfractions.filter(
        (subfraction) => subfraction !== this
      );
    }

    return adjacentFractions;
  }

  @computed public get minRemainingWeight(): number {
    return this.weight - this.rejected;
  }

  @computed public get maxGreen(): number {
    let maxGreen = 0;

    if (this.parent && this.root) {
      const subtractedWeight = this.adjacentFractions.reduce(
        (total, subfraction) => total - subfraction.weight,
        Math.max(this.parent.minRemainingWeight, 0)
      );

      maxGreen = Math.max(Math.min(subtractedWeight, this.parent.maxGreen), 0);
    }

    return maxGreen;
  }

  @computed public get maxSettableWeightForFraction(): number {
    // Parent Green or last output max weight

    if (this.parent && this.root) {
      // subfractions

      if (this.lastReprocess) {
        return this.lastReprocess.weight;
      } else if (this.parent.splitMethod) {
        // PLA-860 v2 (D4): en una division nueva el rechazo de la madre no resta.
        const subtractedWeight = this.adjacentFractions.reduce(
          (total, subfraction) => total - subfraction.weight,
          this.parent.splittableWeight
        );

        return Math.max(subtractedWeight, 0);
      } else {
        const subtractedWeight = this.adjacentFractions.reduce(
          (total, subfraction) => total - subfraction.weight,
          Math.max(this.parent.weight - this.parent.rejected, 0)
        );

        return Math.max(
          Math.min(subtractedWeight, this.parent.maxSettableWeightForFraction),
          0
        );
      }
    } else {
      // main fractions
      return this.minRemainingWeight;
    }
  }

  @computed public get exceedsParentMaxWeight(): boolean {
    if (this.isMain && this.fractionsStore) {
      return this.weight > this.fractionsStore.remainingFractionWeight(this);
    } else {
      return this.weight > this.maxSettableWeightForFraction;
    }
  }

  @computed public get exceedsRejectedMaxWeight(): boolean {
    return this.rejected > this.weight;
  }

  @computed public get remainingInputWeight(): number | '' {
    return !this.rejected ? '' : this.rejected;
  }

  @computed get lastReprocess(): Reprocess | undefined {
    let reprocesses = this.reprocesses;

    if (this.parent && this.root) {
      reprocesses = this.root.reprocesses;
    }

    return reprocesses[reprocesses.length - 1];
  }

  @computed get isReprocessable(): boolean {
    let isReprocessable =
      !!this.sample?.current_qa_physical_analysis.failed && this.totalGreen > 0;

    if (this.lastReprocess) {
      isReprocessable = this.lastReprocess.isReprocessable;
    }

    return isReprocessable;
  }

  @computed get totalRejected(): number {
    return this.subfractions.reduce(
      (totalRejected, subfraction) => totalRejected + subfraction.totalRejected,
      this.rejected
    );
  }

  @computed get totalLoss(): number {
    return this.reprocesses.reduce(
      (totalLoss, reprocess) => totalLoss + reprocess.totalLoss,
      0
    );
  }

  @computed get totalGreen(): number {
    return this.weight - this.totalRejected - this.totalLoss;
  }
}

export default Fraction;
